import dayjs from 'dayjs';
import startCase from 'lodash/startCase';
import { DEFAULT_ORG_ADMIN_JOB_TITLE } from 'config/const';
import { IApiList, ICollectedTipsData, IEmployee, IEmployeePayrollTip, IExportPayout, IIntegration, ILedgerEntry, IntegrationType, IPaymentAdjustment, IPayout, IReceivedTipsData, LedgerEntryType } from 'types';
import { centsToDollars } from 'utils/currency';
import { isOrgAdmin } from './employee';
import { formatJobTitle } from './jobs';
import { toNegative } from './number';
import { toTitleCase } from './strings';

export interface IExportCSV {
  headers: string[];
  data: (string | number)[][];
}

interface IADPRunEntry {
  employeeId: string;
  earningsCode: string | undefined;
  amount: number;
}

interface IPaylocityEntry {
  employeeId: string;
  det: string | undefined;
  detCode: string | undefined;
  amount: number;
}

interface IRestaurant365Entry {
  workerId: string;
  firstName: string;
  lastName: string;
  fullName: string;
  date: string;
  location: string;
  jobTitle: string;
  earningType: string;
  payRate: string;
  hours: string;
  amount: number;
  note: string;
  holdback: number;
}

const BASE_PAYOUT_HEADERS = ['Gross', 'Holdback', 'Net', 'Business Date', 'Date Submitted', 'Last Update', 'Sent By', 'Site', 'Job Title', 'Classification', 'Note', 'Disbursement'];
export const BANK_REC_SUMMARY = ['StartingBalance', 'Credits', 'Debits', 'EndingBalance'];
export const BANK_REC_DAILY = ['Date', 'Amount', 'Entry Type'];
export const BANK_REC_DETAILS = ['Date', 'Amount', 'Description', 'Employee', 'Transaction ID', 'Notes', 'Entry Type'];
export const LEDGER_HEADERS = ['SeqNum', 'Date', 'Type', 'Description', 'Notes', 'Employee', 'Transaction ID', 'Amount', 'New Balance'];
export const PAYROLL_TIPS_HEADERS = ['First Name', 'Last Name', 'Employee ID', 'Tips', 'Autograts', 'Gross', 'Holdback', 'Net', 'Instant', 'Payroll', 'Cash'];
export const USER_PAYOUT_HEADERS = ['Payment ID', 'Status', ...BASE_PAYOUT_HEADERS];
export const PAYOUT_HEADERS = ['First Name', 'Last Name', 'Employee ID', 'Payment ID', 'Status', 'Tips', 'Autograts', ...BASE_PAYOUT_HEADERS];
export const COLLECTED_TIPS_PAYOUT_HEADERS = ['Employee Name', 'Day Segment', 'Job', 'Cash Tips', 'Cash Autograts', 'CC Tips', 'CC Autograts', 'CC Fee', 'Net'];
export const RECEIVED_TIPS_PAYOUT_HEADERS = ['Employee Name', 'Day Segment', 'Job', 'Duration', 'Received From', 'Received Tips', 'Received Autograts'];
export const PAYMENT_ADJUSTMENTS_HEADERS = ['First Name', 'Last Name', 'Employee ID', 'Original', 'Recalculated', 'Difference', 'Tips', 'Autograts'];

// ADP Run Headers
export const ADP_RUN_GENERIC_HEADERS = ['##GENERIC##V1.0'];
export const ADP_RUN_HEADERS = ['Branch Code/ADP Code', 'Pay Frequency', 'Pay Period Start Date', 'Pay Period End Date', 'Employee ID', 'Earnings Code', 'Pay Hours', 'Dollars', 'Separate Check', 'Worked In Dept', 'Rate Code'];

export enum ADPRunExportConfigKey {
  BRANCH_CODE = 'branch_code',
  PAY_FREQUENCY_CODE = 'pay_frequency_code',
  RATE_CODE = 'rate_code',
  INSTANT_TIPS_EARNINGS_CODE = 'instant_tips_earnings_code',
  INSTANT_AUTO_GRATUITY_EARNINGS_CODE = 'instant_auto_gratuity_earnings_code',
  PAYROLL_TIPS_EARNINGS_CODE = 'payroll_tips_earnings_code',
  PAYROLL_AUTO_GRATUITY_EARNINGS_CODE = 'payroll_auto_gratuity_earnings_code',
  CASH_TIPS_EARNINGS_CODE = 'cash_tips_earnings_code',
  CASH_AUTO_GRATUITY_EARNINGS_CODE = 'cash_auto_gratuity_earnings_code',
  HOLDBACKS_EARNINGS_CODE = 'holdbacks_earnings_code',
}

export enum PaylocityExportConfigKey {
  COMPANY_ID = 'company_id',
  INSTANT_TIPS_EARNINGS_CODE = 'instant_tips_earnings_code',
  INSTANT_AUTO_GRATUITY_EARNINGS_CODE = 'instant_auto_gratuity_earnings_code',
  PAYROLL_TIPS_EARNINGS_CODE = 'payroll_tips_earnings_code',
  PAYROLL_AUTO_GRATUITY_EARNINGS_CODE = 'payroll_auto_gratuity_earnings_code',
  CASH_TIPS_EARNINGS_CODE = 'cash_tips_earnings_code',
  CASH_AUTO_GRATUITY_EARNINGS_CODE = 'cash_auto_gratuity_earnings_code',
  HOLDBACKS_EARNINGS_CODE = 'holdbacks_earnings_code',
}

export const RESTAURANT365_HEADERS = ['Worker ID', 'First Name', 'Last Name', 'Full Name', 'Date', 'Location', 'Job Title', 'Earning Type', 'Pay Rate', 'Hours', 'Amount', 'Note'];

export enum Restaurant365ExportConfigKey {
  LOCATION = 'location',
  INSTANT_TIPS_EARNINGS_CODE = 'instant_tips_earnings_code',
  INSTANT_AUTO_GRATUITY_EARNINGS_CODE = 'instant_auto_gratuity_earnings_code',
  PAYROLL_TIPS_EARNINGS_CODE = 'payroll_tips_earnings_code',
  PAYROLL_AUTO_GRATUITY_EARNINGS_CODE = 'payroll_auto_gratuity_earnings_code',
  CASH_TIPS_EARNINGS_CODE = 'cash_tips_earnings_code',
  CASH_AUTO_GRATUITY_EARNINGS_CODE = 'cash_auto_gratuity_earnings_code',
  HOLDBACKS_EARNINGS_CODE = 'holdbacks_earnings_code',
}

export enum PayrollExportValidationType {
  DUPLICATE_EMPLOYEE_ID = 'duplicate_employee_id',
  MISSING_EMPLOYEE_ID = 'missing_employee_id',
  NO_EARNINGS_CODE = 'no_earnings_code',
}

export enum MissingEarningsCode {
  BRANCH_CODE = 'Branch Code',
  COMPANY_ID = 'Company ID',
  LOCATION = 'Location',
  PAY_FREQUENCY_CODE = 'Pay Frequency Code',
  RATE_CODE = 'Rate Code',
  INSTANT_TIPS = 'Instant Tips',
  INSTANT_AUTO_GRATUITY = 'Instant Auto Gratuity',
  PAYROLL_TIPS = 'Payroll Tips',
  PAYROLL_AUTO_GRATUITY = 'Payroll Auto Gratuity',
  CASH_TIPS = 'Cash Tips',
  CASH_AUTO_GRATUITY = 'Cash Auto Gratuity',
  HOLDBACKS = 'Holdbacks',
}

export interface IPayrollDataError {
  employeeId?: string;
  employeeName?: string | string[] | { id: string; name: string }[];
}

export interface IPayrollIntegrationExportError {
  type: PayrollExportValidationType;
  data?: IPayrollDataError[] | MissingEarningsCode[] | IEmployeePayrollTip[];
}

export class PayrollExportError extends Error {
  error?: IPayrollIntegrationExportError;

  constructor(error?: IPayrollIntegrationExportError) {
    super('Export payroll data error.');
    this.error = error;
  }
}

export class CSVFormatter {
  public static ledger = (entries: IApiList<ILedgerEntry>): IExportCSV => ({
    headers: LEDGER_HEADERS,
    data: entries.data?.map((entry) => [
      entry.seqNum.toString(),
      dayjs(entry.createdAt).format('YYYY-MM-DD HH:mm:ss z'),
      entry.entryType,
      entry.description,
      entry.txnRefNotes || '',
      entry.employee?.name ?? '',
      entry.txnRefId ?? '',
      entry.entryType === LedgerEntryType.DEBIT ? -centsToDollars(entry.amount) : centsToDollars(entry.amount),
      centsToDollars(entry.balance),
    ]),
  });

  public static payouts = (entries: IExportPayout[]): IExportCSV => ({
    headers: PAYOUT_HEADERS,
    data: entries.map((entry) => [
      entry.firstName,
      entry.lastName,
      entry.custEmployeeId || '',
      entry.id,
      toTitleCase(entry.status),
      centsToDollars(entry.gross - entry.autoGratuity),
      centsToDollars(entry.autoGratuity),
      centsToDollars(entry.gross),
      centsToDollars(toNegative(entry.holdback)),
      centsToDollars(entry.net),
      entry.paymentDate,
      dayjs(entry.createdAt).format('YYYY-MM-DD HH:mm:ss z'),
      dayjs(entry.updatedAt).format('YYYY-MM-DD HH:mm:ss z'),
      entry.submittedBy || '',
      entry.siteName,
      toTitleCase(entry.jobTitle),
      entry.jobClassification || '',
      entry.notes || '',
      toTitleCase(entry.disbursementMethod || ''),
    ]),
  });

  public static employeePayouts = (employee: IEmployee, entries: IPayout[]): IExportCSV => ({
    headers: PAYOUT_HEADERS,
    data: entries.map((entry) => {
      const employeeId = (employee?.sites || []).find((s) => s.id === entry.site.id)?.employeeId || '';
      const submittedBy = entry.statusHistory?.find((status) => status.action === 'create')?.changedBy.name || '';

      return [
        entry.employee.firstName,
        entry.employee.lastName,
        employeeId,
        entry.id,
        toTitleCase(entry.status),
        centsToDollars(entry.gross - entry.autoGratuity),
        centsToDollars(entry.autoGratuity),
        centsToDollars(entry.gross),
        centsToDollars(toNegative(entry.holdback)),
        centsToDollars(entry.net),
        entry.paymentDate,
        dayjs(entry.createdAt).format('YYYY-MM-DD HH:mm:ss z'),
        dayjs(entry.updatedAt).format('YYYY-MM-DD HH:mm:ss z'),
        submittedBy || '',
        entry.site.name,
        entry.job.title,
        entry.job.classification || '',
        entry.notes || '',
        entry.disbursementMethod || '',
      ];
    }),
  });

  public static userPayouts = (entries: IPayout[]): IExportCSV => ({
    headers: USER_PAYOUT_HEADERS,
    data: entries.map((entry) => {
      const submittedBy = entry.statusHistory?.find((status) => status.action === 'create')?.changedBy.name || '';

      return [
        entry.id,
        toTitleCase(entry.status),
        centsToDollars(entry.gross),
        centsToDollars(toNegative(entry.holdback)),
        centsToDollars(entry.net),
        entry.paymentDate,
        dayjs(entry.createdAt).format('YYYY-MM-DD HH:mm:ss z'),
        dayjs(entry.updatedAt).format('YYYY-MM-DD HH:mm:ss z'),
        submittedBy,
        entry.site.name,
        entry.job.title,
        entry.job.classification || '',
        entry.notes || '',
        entry.disbursementMethod || '',
      ];
    }),
  });

  public static collectedTipsPayout = (entries: ICollectedTipsData[]): IExportCSV => ({
    headers: COLLECTED_TIPS_PAYOUT_HEADERS,
    data: entries.map((entry) => [
      entry.payee || '',
      entry.daySegment || '',
      toTitleCase(entry.jobTitle || ''),
      centsToDollars(entry.cashTips),
      centsToDollars(entry.cashAutograts),
      centsToDollars(entry.ccTips),
      centsToDollars(entry.ccAutograts),
      centsToDollars(entry.ccFee),
      centsToDollars(entry.net),
    ]),
  });

  public static receivedTipsPayout = (entries: IReceivedTipsData[]): IExportCSV => ({
    headers: RECEIVED_TIPS_PAYOUT_HEADERS,
    data: entries.map((entry) => [
      entry.payee || '',
      entry.daySegment || '',
      toTitleCase(entry.jobTitle || ''),
      entry.duration || '',
      toTitleCase(entry.receivedFrom),
      centsToDollars(entry.receivedTips),
      centsToDollars(entry.receivedAutograts),
    ]),
  });

  public static paymentAdjustments = (entries: IPaymentAdjustment[]): IExportCSV => ({
    headers: PAYMENT_ADJUSTMENTS_HEADERS,
    data: entries.map((entry) => [
      entry.firstName,
      entry.lastName,
      entry.employeeId || '',
      centsToDollars(entry.original),
      centsToDollars(entry.recalculated),
      centsToDollars(entry.difference),
      centsToDollars(entry.tips),
      centsToDollars(entry.autograts),
    ]),
  });

  public static bankRecSummary = (startingBalance: number, endingBalance: number, totalCredits: number, totalDebits: number): IExportCSV => ({
    headers: BANK_REC_SUMMARY,
    data: [[centsToDollars(startingBalance), centsToDollars(totalCredits), -centsToDollars(totalDebits), centsToDollars(endingBalance)]],
  });

  public static bankRecDaily = (credits: { date: string; amount: number }[], debits: { date: string; amount: number }[]): IExportCSV => ({
    headers: BANK_REC_DAILY,
    data: [
      ...credits.map((entry) => [dayjs(entry.date, 'MM-DD-YYYY').format('YYYY-MM-DD'), centsToDollars(entry.amount), 'Credit']),
      ...debits.map((entry) => [dayjs(entry.date, 'MM-DD-YYYY').format('YYYY-MM-DD'), -centsToDollars(entry.amount), 'Debit']),
    ],
  });

  public static bankRecDetails = (credits: ILedgerEntry[], debits: ILedgerEntry[]): IExportCSV => ({
    headers: BANK_REC_DETAILS,
    data: [
      ...credits.map((entry) => [dayjs(entry.createdAt).format('YYYY-MM-DD'), centsToDollars(entry.amount), entry.description, '', '', entry.txnRefNotes || '', startCase(entry.entryType)]),
      ...debits.map((entry) => [dayjs(entry.createdAt).format('YYYY-MM-DD'), -centsToDollars(entry.amount), entry.description, entry.employee?.name ?? '', entry.txnRefId || '', entry.txnRefNotes || '', startCase(entry.entryType)]),
    ],
  });

  public static employeePayrollTips = (siteId: string, startDate: string, endDate: string, entries: IEmployeePayrollTip[], integration?: IIntegration): IExportCSV => {
    switch (integration?.type) {
      case IntegrationType.ADP_RUN_EXPORT:
        return CSVFormatter.formatAdpRunPayrollTips(siteId, startDate, endDate, entries, integration);
      case IntegrationType.PAYLOCITY_EXPORT:
        return CSVFormatter.formatPaylocityPayrollTips(siteId, entries, integration);
      case IntegrationType.RESTAURANT365_EXPORT:
        return CSVFormatter.formatRestaurant365PayrollTips(siteId, startDate, endDate, entries, integration);
      default:
        return {
          headers: PAYROLL_TIPS_HEADERS,
          data: entries?.map((entry) => {
            const siteEmployeeId = (entry.employee.sites || []).find((s) => s.id === siteId)?.employeeId ?? '';
            return [
              entry.employee.firstName,
              entry.employee.lastName,
              siteEmployeeId,
              centsToDollars(entry.instant.tips + entry.payroll.tips + entry.cash.tips),
              centsToDollars(entry.instant.autoGratuities + entry.payroll.autoGratuities + entry.cash.autoGratuities),
              centsToDollars(entry.gross),
              centsToDollars(toNegative(entry.holdback)),
              centsToDollars(entry.net),
              centsToDollars(entry.instant.total - entry.holdback),
              centsToDollars(entry.payroll.total),
              centsToDollars(entry.cash.total),
            ];
          }),
        };
    }
  };

  private static formatAdpRunPayrollTips(siteId: string, startDate: string, endDate: string, entries: IEmployeePayrollTip[], integration: IIntegration): IExportCSV {
    // Get the config for the integration
    const configMap = new Map(integration.config.map((config) => [config.key, { value: config.value, ignore: config.ignore || false }]));
    const branchCode = configMap.get(ADPRunExportConfigKey.BRANCH_CODE);
    const payFrequencyCode = configMap.get(ADPRunExportConfigKey.PAY_FREQUENCY_CODE);
    const rateCode = configMap.get(ADPRunExportConfigKey.RATE_CODE);
    const instantTipsEarningsCode = configMap.get(ADPRunExportConfigKey.INSTANT_TIPS_EARNINGS_CODE);
    const instantAutoGratuitiesEarningsCode = configMap.get(ADPRunExportConfigKey.INSTANT_AUTO_GRATUITY_EARNINGS_CODE);
    const payrollTipsEarningsCode = configMap.get(ADPRunExportConfigKey.PAYROLL_TIPS_EARNINGS_CODE);
    const payrollAutoGratuitiesEarningsCode = configMap.get(ADPRunExportConfigKey.PAYROLL_AUTO_GRATUITY_EARNINGS_CODE);
    const cashTipsEarningsCode = configMap.get(ADPRunExportConfigKey.CASH_TIPS_EARNINGS_CODE);
    const cashAutoGratuitiesEarningsCode = configMap.get(ADPRunExportConfigKey.CASH_AUTO_GRATUITY_EARNINGS_CODE);
    const holdbacksEarningsCode = configMap.get(ADPRunExportConfigKey.HOLDBACKS_EARNINGS_CODE);

    // Format the start and end dates
    const startDateFormatted = dayjs(startDate).format('MM/DD/YYYY');
    const endDateFormatted = dayjs(endDate).format('MM/DD/YYYY');

    // Use a Map to group and sum amounts by employee and earnings code.
    const groupedData = new Map<string, IADPRunEntry>();

    entries?.forEach((entry) => {
      const employeeRef = this.getExportPayrollEmployeeRef(entry, siteId);

      const updateGroupedData = (earningsCode: string, amount: number, ignore: boolean = false) => {
        const key = `${entry.employee.id}-${earningsCode}`;
        if (ignore) return; // if the earnings code is ignored, don't add it to the grouped data

        if (groupedData.has(key)) {
          groupedData.get(key).amount += amount;
        } else {
          groupedData.set(key, { employeeId: employeeRef, earningsCode, amount });
        }
      };

      // Instant Tips
      if (entry.instant.tips > 0) updateGroupedData(instantTipsEarningsCode.value, entry.instant.tips, instantTipsEarningsCode.ignore);

      // Instant Auto Gratuities
      if (entry.instant.autoGratuities > 0) updateGroupedData(instantAutoGratuitiesEarningsCode.value, entry.instant.autoGratuities, instantAutoGratuitiesEarningsCode.ignore);

      // Payroll Tips
      if (entry.payroll.tips > 0) updateGroupedData(payrollTipsEarningsCode.value, entry.payroll.tips, payrollTipsEarningsCode.ignore);

      // Payroll Auto Gratuities
      if (entry.payroll.autoGratuities > 0) updateGroupedData(payrollAutoGratuitiesEarningsCode.value, entry.payroll.autoGratuities, payrollAutoGratuitiesEarningsCode.ignore);

      // Cash Tips
      if (entry.cash.tips > 0) updateGroupedData(cashTipsEarningsCode.value, entry.cash.tips, cashTipsEarningsCode.ignore);

      // Cash Auto Gratuities
      if (entry.cash.autoGratuities > 0) updateGroupedData(cashAutoGratuitiesEarningsCode.value, entry.cash.autoGratuities, cashAutoGratuitiesEarningsCode.ignore);

      // Holdbacks
      if (entry.holdback > 0) updateGroupedData(holdbacksEarningsCode.value, entry.holdback, holdbacksEarningsCode.ignore);
    });

    // Convert the grouped data Map into CSV rows.
    const csvData = Array.from(groupedData.values()).map((item) => [
      branchCode?.value,
      payFrequencyCode?.value,
      startDateFormatted,
      endDateFormatted,
      item.employeeId,
      item.earningsCode,
      '',
      centsToDollars(item.amount),
      '0',
      '',
      rateCode?.value,
    ]);

    return {
      headers: ADP_RUN_GENERIC_HEADERS, // ADP Run has a generic header and then the headers for the payroll tips
      data: [ADP_RUN_HEADERS, ...csvData],
    };
  }

  private static formatPaylocityPayrollTips(siteId: string, entries: IEmployeePayrollTip[], integration: IIntegration): IExportCSV {
    const EARNINGS = 'E'; // Paylocity always uses E (Earnings)
    const DEDUCTIONS = 'D'; // Paylocity always uses D (Deductions)

    // Get the config for the integration
    const configMap = new Map(integration.config.map((config) => [config.key, { value: config.value, ignore: config.ignore || false }]));
    const companyId = configMap.get(PaylocityExportConfigKey.COMPANY_ID);
    const instantTipsDet = EARNINGS; // Paylocity always uses E (Earnings)for instant tips
    const instantTipsDetCode = configMap.get(PaylocityExportConfigKey.INSTANT_TIPS_EARNINGS_CODE);
    const instantAutoGratuitiesDet = EARNINGS; // Paylocity always uses E (Earnings)for instant auto gratuities
    const instantAutoGratuitiesDetCode = configMap.get(PaylocityExportConfigKey.INSTANT_AUTO_GRATUITY_EARNINGS_CODE);
    const payrollTipsDet = EARNINGS; // Paylocity always uses E (Earnings)for payroll tips
    const payrollTipsDetCode = configMap.get(PaylocityExportConfigKey.PAYROLL_TIPS_EARNINGS_CODE);
    const payrollAutoGratuitiesDet = EARNINGS; // Paylocity always uses E (Earnings)for payroll auto gratuities
    const payrollAutoGratuitiesDetCode = configMap.get(PaylocityExportConfigKey.PAYROLL_AUTO_GRATUITY_EARNINGS_CODE);
    const cashTipsDet = EARNINGS; // Paylocity always uses E (Earnings)for cash tips
    const cashTipsDetCode = configMap.get(PaylocityExportConfigKey.CASH_TIPS_EARNINGS_CODE);
    const cashAutoGratuitiesDet = EARNINGS; // Paylocity always uses E (Earnings)for cash auto gratuities
    const cashAutoGratuitiesDetCode = configMap.get(PaylocityExportConfigKey.CASH_AUTO_GRATUITY_EARNINGS_CODE);
    const holdbacksDet = DEDUCTIONS; // Paylocity always uses D (Deductions)for holdbacks
    const holdbacksDetCode = configMap.get(PaylocityExportConfigKey.HOLDBACKS_EARNINGS_CODE);

    // Use a Map to group and sum amounts by employee, det, and detCode.
    const groupedData = new Map<string, IPaylocityEntry>();

    entries?.forEach((entry) => {
      const employeeRef = this.getExportPayrollEmployeeRef(entry, siteId);

      const updateGroupedData = (det: string, detCode: string, amount: number, ignore: boolean = false) => {
        if (ignore) return; // if the earnings code is ignored, don't add it to the grouped data

        const key = `${entry.employee.id}-${det}-${detCode}`;
        if (groupedData.has(key)) {
          groupedData.get(key).amount += amount;
        } else {
          groupedData.set(key, { employeeId: employeeRef, det, detCode, amount });
        }
      };

      // Instant Tips
      if (entry.instant.tips > 0) updateGroupedData(instantTipsDet, instantTipsDetCode.value, entry.instant.tips, instantTipsDetCode.ignore);

      // Instant Auto Gratuities
      if (entry.instant.autoGratuities > 0) updateGroupedData(instantAutoGratuitiesDet, instantAutoGratuitiesDetCode.value, entry.instant.autoGratuities, instantAutoGratuitiesDetCode.ignore);

      // Payroll Tips
      if (entry.payroll.tips > 0) updateGroupedData(payrollTipsDet, payrollTipsDetCode.value, entry.payroll.tips, payrollTipsDetCode.ignore);

      // Payroll Auto Gratuities
      if (entry.payroll.autoGratuities > 0) updateGroupedData(payrollAutoGratuitiesDet, payrollAutoGratuitiesDetCode.value, entry.payroll.autoGratuities, payrollAutoGratuitiesDetCode.ignore);

      // Cash Tips
      if (entry.cash.tips > 0) updateGroupedData(cashTipsDet, cashTipsDetCode.value, entry.cash.tips, cashTipsDetCode.ignore);

      // Cash Auto Gratuities
      if (entry.cash.autoGratuities > 0) updateGroupedData(cashAutoGratuitiesDet, cashAutoGratuitiesDetCode.value, entry.cash.autoGratuities, cashAutoGratuitiesDetCode.ignore);

      // Holdbacks
      if (entry.holdback > 0) updateGroupedData(holdbacksDet, holdbacksDetCode.value, entry.holdback, holdbacksDetCode.ignore);
    });

    // Convert the grouped data Map into CSV rows.
    const csvData = Array.from(groupedData.values()).map((item) => [companyId.value, item.employeeId, item.det, item.detCode, '', centsToDollars(item.amount), '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '']);

    return {
      headers: [], // Paylocity doesn't have any headers for payroll tips
      data: csvData,
    };
  }

  private static formatRestaurant365PayrollTips(siteId: string, startDate: string, endDate: string, entries: IEmployeePayrollTip[], integration: IIntegration): IExportCSV {
    // Get the config for the integration
    const configMap = new Map(integration.config.map((config) => [config.key, { value: config.value, ignore: config.ignore || false }]));
    const location = configMap.get(Restaurant365ExportConfigKey.LOCATION);
    const instantTipsEarningsCode = configMap.get(Restaurant365ExportConfigKey.INSTANT_TIPS_EARNINGS_CODE);
    const instantAutoGratuitiesEarningsCode = configMap.get(Restaurant365ExportConfigKey.INSTANT_AUTO_GRATUITY_EARNINGS_CODE);
    const payrollTipsEarningsCode = configMap.get(Restaurant365ExportConfigKey.PAYROLL_TIPS_EARNINGS_CODE);
    const payrollAutoGratuitiesEarningsCode = configMap.get(Restaurant365ExportConfigKey.PAYROLL_AUTO_GRATUITY_EARNINGS_CODE);
    const cashTipsEarningsCode = configMap.get(Restaurant365ExportConfigKey.CASH_TIPS_EARNINGS_CODE);
    const cashAutoGratuitiesEarningsCode = configMap.get(Restaurant365ExportConfigKey.CASH_AUTO_GRATUITY_EARNINGS_CODE);
    const holdbacksEarningsCode = configMap.get(Restaurant365ExportConfigKey.HOLDBACKS_EARNINGS_CODE);

    // Format the end date
    const endDateFormatted = dayjs(endDate).format('MM/DD/YYYY');

    // Use a Map to group and sum amounts by employee and earnings code.
    const groupedData = new Map<string, IRestaurant365Entry>();

    entries?.forEach((entry) => {
      const employeeRef = this.getExportPayrollEmployeeRef(entry, siteId);
      const jobTitle = isOrgAdmin(entry.employee) ? DEFAULT_ORG_ADMIN_JOB_TITLE : (entry.employee.sites?.find((s) => s.id === siteId)?.job.title ?? '');

      const updateGroupedData = (earningsCode: string, amount: number, holdback?: number, ignore: boolean = false) => {
        if (ignore) return; // if the earnings code is ignored, don't add it to the grouped data

        const key = `${entry.employee.id}-${earningsCode}`;
        if (groupedData.has(key)) {
          groupedData.get(key).amount += amount;
          groupedData.get(key).holdback += holdback || 0;
        } else {
          groupedData.set(key, {
            workerId: employeeRef,
            firstName: entry.employee.firstName,
            lastName: entry.employee.lastName,
            fullName: entry.employee.name,
            date: endDateFormatted,
            location: location.value,
            jobTitle: formatJobTitle(jobTitle),
            earningType: earningsCode,
            payRate: '',
            hours: '',
            amount,
            holdback: holdback || 0,
            note: '',
          });
        }
      };

      // Instant Tips
      const instantTips = entry.instant.tips;
      if (instantTips > 0) updateGroupedData(instantTipsEarningsCode.value, instantTips, entry.holdback, instantTipsEarningsCode.ignore);

      // Instant Auto Gratuities
      const instantAutoGratuities = entry.instant.autoGratuities;
      if (instantAutoGratuities > 0) updateGroupedData(instantAutoGratuitiesEarningsCode.value, instantAutoGratuities, !instantTips && entry.holdback, instantAutoGratuitiesEarningsCode.ignore);

      // Payroll Tips
      if (entry.payroll.tips > 0) updateGroupedData(payrollTipsEarningsCode.value, entry.payroll.tips, undefined, payrollTipsEarningsCode.ignore);

      // Payroll Auto Gratuities
      if (entry.payroll.autoGratuities > 0) updateGroupedData(payrollAutoGratuitiesEarningsCode.value, entry.payroll.autoGratuities, undefined, payrollAutoGratuitiesEarningsCode.ignore);

      // Cash Tips
      if (entry.cash.tips > 0) updateGroupedData(cashTipsEarningsCode.value, entry.cash.tips, undefined, cashTipsEarningsCode.ignore);

      // Cash Auto Gratuities
      if (entry.cash.autoGratuities > 0) updateGroupedData(cashAutoGratuitiesEarningsCode.value, entry.cash.autoGratuities, undefined, cashAutoGratuitiesEarningsCode.ignore);
    });

    // Convert the grouped data Map into CSV rows.
    const csvData = Array.from(groupedData.values()).map((item) => [
      item.workerId,
      toTitleCase(item.firstName),
      toTitleCase(item.lastName),
      toTitleCase(item.fullName),
      endDateFormatted,
      item.location,
      item.jobTitle,
      item.earningType,
      item.payRate,
      item.hours,
      centsToDollars(item.amount),
      item.note,
      ...(holdbacksEarningsCode?.value ? [item.holdback ? centsToDollars(item.holdback) : ''] : []), // only add the holdback if it's not 0 and we have a holdbacks earnings code
    ]);

    return {
      headers: holdbacksEarningsCode?.value ? [...RESTAURANT365_HEADERS, holdbacksEarningsCode.value] : RESTAURANT365_HEADERS, // only add the holdbacks earnings code if we have a holdbacks earnings code
      data: csvData,
    };
  }

  public static validateExportPayroll(siteId: string, entries: IEmployeePayrollTip[], integration: IIntegration): IPayrollIntegrationExportError | null {
    this.validateExportPayrollConfig(entries, integration);
    const validationWarning = this.validateExportPayrollEmployeeIDs(siteId, entries);
    return validationWarning;
  }

  private static validateExportPayrollConfig(entries: IEmployeePayrollTip[], integration: IIntegration) {
    const missingConfig: MissingEarningsCode[] = [];
    const { hasInstantTips, hasPayrollTips, hasInstantAutoGratuities, hasPayrollAutoGratuities, hasHoldbacks, hasCashTips, hasCashAutoGratuities } = CSVFormatter.determineActiveEarningTypes(entries);

    // Create a map for easier config lookup
    const configMap = new Map(integration.config?.map((config) => [config.key, config.value]));

    const checkConfig = (configKeys: string[], errorCode: MissingEarningsCode, condition = true) => {
      if (condition && configKeys.some((key) => !configMap.get(key))) {
        missingConfig.push(errorCode);
      }
    };

    switch (integration.type) {
      case IntegrationType.ADP_RUN_EXPORT:
        checkConfig([ADPRunExportConfigKey.BRANCH_CODE], MissingEarningsCode.BRANCH_CODE);
        checkConfig([ADPRunExportConfigKey.PAY_FREQUENCY_CODE], MissingEarningsCode.PAY_FREQUENCY_CODE);
        checkConfig([ADPRunExportConfigKey.RATE_CODE], MissingEarningsCode.RATE_CODE);
        checkConfig([ADPRunExportConfigKey.INSTANT_TIPS_EARNINGS_CODE], MissingEarningsCode.INSTANT_TIPS, hasInstantTips);
        checkConfig([ADPRunExportConfigKey.INSTANT_AUTO_GRATUITY_EARNINGS_CODE], MissingEarningsCode.INSTANT_AUTO_GRATUITY, hasInstantAutoGratuities);
        checkConfig([ADPRunExportConfigKey.PAYROLL_TIPS_EARNINGS_CODE], MissingEarningsCode.PAYROLL_TIPS, hasPayrollTips);
        checkConfig([ADPRunExportConfigKey.PAYROLL_AUTO_GRATUITY_EARNINGS_CODE], MissingEarningsCode.PAYROLL_AUTO_GRATUITY, hasPayrollAutoGratuities);
        checkConfig([ADPRunExportConfigKey.CASH_TIPS_EARNINGS_CODE], MissingEarningsCode.CASH_TIPS, hasCashTips);
        checkConfig([ADPRunExportConfigKey.CASH_AUTO_GRATUITY_EARNINGS_CODE], MissingEarningsCode.CASH_AUTO_GRATUITY, hasCashAutoGratuities);
        checkConfig([ADPRunExportConfigKey.HOLDBACKS_EARNINGS_CODE], MissingEarningsCode.HOLDBACKS, hasHoldbacks);
        break;
      case IntegrationType.PAYLOCITY_EXPORT:
        checkConfig([PaylocityExportConfigKey.COMPANY_ID], MissingEarningsCode.COMPANY_ID);
        checkConfig([PaylocityExportConfigKey.INSTANT_TIPS_EARNINGS_CODE], MissingEarningsCode.INSTANT_TIPS, hasInstantTips);
        checkConfig([PaylocityExportConfigKey.INSTANT_AUTO_GRATUITY_EARNINGS_CODE], MissingEarningsCode.INSTANT_AUTO_GRATUITY, hasInstantAutoGratuities);
        checkConfig([PaylocityExportConfigKey.PAYROLL_TIPS_EARNINGS_CODE], MissingEarningsCode.PAYROLL_TIPS, hasPayrollTips);
        checkConfig([PaylocityExportConfigKey.PAYROLL_AUTO_GRATUITY_EARNINGS_CODE], MissingEarningsCode.PAYROLL_AUTO_GRATUITY, hasPayrollAutoGratuities);
        checkConfig([PaylocityExportConfigKey.CASH_TIPS_EARNINGS_CODE], MissingEarningsCode.CASH_TIPS, hasCashTips);
        checkConfig([PaylocityExportConfigKey.CASH_AUTO_GRATUITY_EARNINGS_CODE], MissingEarningsCode.CASH_AUTO_GRATUITY, hasCashAutoGratuities);
        checkConfig([PaylocityExportConfigKey.HOLDBACKS_EARNINGS_CODE], MissingEarningsCode.HOLDBACKS, hasHoldbacks);
        break;
      case IntegrationType.RESTAURANT365_EXPORT:
        checkConfig([Restaurant365ExportConfigKey.LOCATION], MissingEarningsCode.LOCATION);
        checkConfig([Restaurant365ExportConfigKey.INSTANT_TIPS_EARNINGS_CODE], MissingEarningsCode.INSTANT_TIPS, hasInstantTips);
        checkConfig([Restaurant365ExportConfigKey.INSTANT_AUTO_GRATUITY_EARNINGS_CODE], MissingEarningsCode.INSTANT_AUTO_GRATUITY, hasInstantAutoGratuities);
        checkConfig([Restaurant365ExportConfigKey.PAYROLL_TIPS_EARNINGS_CODE], MissingEarningsCode.PAYROLL_TIPS, hasPayrollTips);
        checkConfig([Restaurant365ExportConfigKey.PAYROLL_AUTO_GRATUITY_EARNINGS_CODE], MissingEarningsCode.PAYROLL_AUTO_GRATUITY, hasPayrollAutoGratuities);
        checkConfig([Restaurant365ExportConfigKey.CASH_TIPS_EARNINGS_CODE], MissingEarningsCode.CASH_TIPS, hasCashTips);
        checkConfig([Restaurant365ExportConfigKey.CASH_AUTO_GRATUITY_EARNINGS_CODE], MissingEarningsCode.CASH_AUTO_GRATUITY, hasCashAutoGratuities);
        checkConfig([Restaurant365ExportConfigKey.HOLDBACKS_EARNINGS_CODE], MissingEarningsCode.HOLDBACKS, hasHoldbacks);
        break;
      default:
        break;
    }

    if (missingConfig.length > 0) {
      throw new PayrollExportError({
        type: PayrollExportValidationType.NO_EARNINGS_CODE,
        data: missingConfig,
      });
    }
  }

  private static validateExportPayrollEmployeeIDs(siteId: string, entries: IEmployeePayrollTip[]): IPayrollIntegrationExportError | null {
    const employeesWithMissingIds: IPayrollDataError[] = [];

    // Map to store employeeId and the list of names associated with it
    const employeeIdToNamesMap = new Map<string, string[]>();

    entries.forEach((entry) => {
      const siteEmployee = (entry.employee.sites || []).find((s) => s.id === siteId);
      const employeeId = siteEmployee?.employeeId;

      if (!employeeId) {
        employeesWithMissingIds.push({ employeeName: entry.employee.name });
      } else if (!employeeIdToNamesMap.has(employeeId)) {
        // If employeeId is not in the map, add it
        employeeIdToNamesMap.set(employeeId, [entry.employee.name]);
      } else {
        // Add the current employee's name to the list for this employeeId
        employeeIdToNamesMap.get(employeeId).push(entry.employee.name);
      }
    });

    // Check for duplicate employee IDs
    const duplicateEmployeeIdData: IPayrollDataError[] = [];
    employeeIdToNamesMap.forEach((names, id) => {
      if (names.length > 1) {
        // If more than one name is associated with an employeeId, it's a duplicate
        duplicateEmployeeIdData.push({ employeeId: id, employeeName: names });
      }
    });

    if (duplicateEmployeeIdData.length > 0) {
      throw new PayrollExportError({
        type: PayrollExportValidationType.DUPLICATE_EMPLOYEE_ID,
        data: duplicateEmployeeIdData,
      });
    }

    // Check for missing employee IDs
    if (employeesWithMissingIds.length > 0) {
      return {
        type: PayrollExportValidationType.MISSING_EMPLOYEE_ID,
        data: employeesWithMissingIds,
      };
    }

    return null;
  }

  private static getExportPayrollEmployeeRef(entry: IEmployeePayrollTip, siteId: string) {
    // Return employee id or employee name if id is not present
    return entry.employee.sites?.find((s) => s.id === siteId)?.employeeId || toTitleCase(entry.employee.name);
  }

  private static determineActiveEarningTypes(entries: IEmployeePayrollTip[]) {
    // Check if we have any instant tips, auto gratuities, or holdbacks
    let hasInstantTips = false;
    let hasPayrollTips = false;
    let hasInstantAutoGratuities = false;
    let hasPayrollAutoGratuities = false;
    let hasCashTips = false;
    let hasCashAutoGratuities = false;
    let hasHoldbacks = false;

    entries?.some((entry) => {
      if (entry.instant.tips > 0) hasInstantTips = true;
      if (entry.payroll.tips > 0) hasPayrollTips = true;
      if (entry.instant.autoGratuities > 0) hasInstantAutoGratuities = true;
      if (entry.payroll.autoGratuities > 0) hasPayrollAutoGratuities = true;
      if (entry.holdback > 0) hasHoldbacks = true;
      if (entry.cash.tips > 0) hasCashTips = true;
      if (entry.cash.autoGratuities > 0) hasCashAutoGratuities = true;
      return hasInstantTips && hasPayrollTips && hasInstantAutoGratuities && hasPayrollAutoGratuities && hasHoldbacks;
    });

    return { hasInstantTips, hasPayrollTips, hasInstantAutoGratuities, hasPayrollAutoGratuities, hasHoldbacks, hasCashTips, hasCashAutoGratuities };
  }
}
